import { getModuleFileTwo } from '~src/services/api/modules';
import { DownloadingState, useStore } from '~src/store';
import * as zip from '@zip.js/zip.js';
import streamSaver from 'streamsaver';
import { Modal } from 'antd';
import { TableEntry } from '~components/atoms/FileTables/FileTable.utils';

type ModuleFileDownload = {
  projectId: string;
  moduleFileId: string;
  moduleId: string | null;
  response_type: string;
  originalItem: TableEntry;
};

// --- ZipStream Class ---
class ZipStream {
  public zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  public writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  public totalFiles = 0;
  public totalSize = 0;
  public downloadedSize = 0;
  private currentFileAlreadyStreamed = 0;
  public isCancelled = false;
  private startTime = 0;
  private endTime = 0;
  private fileMap: Map<string, number> = new Map();

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      useStore.getState().totalDownloadFiles(this.totalFiles);
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Project-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isCancelled = true;
    }
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';
    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }
    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(folder: string, name: string, blob: Blob) {
    if (this.isCancelled || !this.zipWriter) return;
    const uniqueName = this.getUniqueFileName(`${folder}${name}`);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniqueName, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      this.downloadedSize += blob.size;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniqueName} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.errorLog.push(
        `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | Modules with Folder`
      );
      useStore
        .getState()
        .addError(
          `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | Modules with Folder`
        );
      this.isCancelled = true;
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isCancelled) return;
      this.endTime = performance.now();
      const totalSizeMB = (this.downloadedSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.downloadedSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      // Merge successes and errors in a structured way
      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      // Construct final log content
      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      await this.zipWriter.add('download_log.txt', new zip.BlobReader(logBlob));
      await this.zipWriter.close();

      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    } catch (error) {
      this.isCancelled = true;
    }
  }
}

// --- Worker Creation ---
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            try {
              const response = await fetch(event.data.fileUrl);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, file: event.data.file, blob });
            } catch (error) {
              self.postMessage({ success: false, file: event.data.file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

// --- Modal Confirm Download ---
const ModalConfirmDownload = (): Promise<boolean> => {
  function onCancelfunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content:
        'Too many files selected (50+ files). There may be download issues. Reduce to 50 max.',
      onOk: () => resolve(true),
      onCancel: () => {
        onCancelfunction();
        resolve(false);
      },
      okText: 'I understand',
      cancelText: 'Cancel',
    });
  });
};

// --- Splits array into chunks of 10 files ---
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

const MAX_WORKERS = 5;

const processChunk = async (
  chunk: ModuleFileDownload[],
  zipStream: ZipStream
) => {
  if (zipStream.isCancelled) return;

  const workers = Array.from({ length: MAX_WORKERS }, () => ({
    worker: createWorker(),
    isBusy: false,
  }));

  const taskQueue = [...chunk];
  let activeTasks = 0;

  const processNext = () => {
    if (taskQueue.length === 0) return;
    if (zipStream.isCancelled) return;

    const availableWorker = workers.find((w) => !w.isBusy);
    if (!availableWorker) return;

    const file = taskQueue.shift();
    if (!file) return;

    availableWorker.isBusy = true;
    activeTasks++;

    getModuleFileTwo({
      projectId: file.projectId,
      moduleFileId: file.moduleFileId,
      moduleId: file.moduleId ? Number(file.moduleId) : 0,
      response_type: 'base64',
    })
      .then((fileUrl) => {
        if (zipStream.isCancelled) return;
        availableWorker.worker.postMessage({ fileUrl, file });
      })
      .catch((error) => {
        zipStream.errorLog.push(
          `${
            file.originalItem.sector
              ? file.originalItem.sector
              : 'Uncategorized'
          }/${file.originalItem.name} (0.00 MB) - ERROR: ${
            error.message
          } | Modules With Folder`
        );
        useStore
          .getState()
          .addError(
            `${
              file.originalItem.sector
                ? file.originalItem.sector
                : 'Uncategorized'
            }/${file.originalItem.name} (0.00 MB) - ERROR: ${
              error.message
            } | Modules With Folder`
          );
        availableWorker.isBusy = false;
        activeTasks--;
        processNext();
      });

    availableWorker.worker.onmessage = async (event) => {
      if (zipStream.isCancelled) return;

      const { success, file: fileData, blob, error } = event.data;

      // folder path
      const folderPath =
        fileData.originalItem.sector && fileData.originalItem.sector !== 'null'
          ? `${fileData.originalItem.sector}/`
          : 'Uncategorized/';

      if (success) {
        await zipStream.addFile(folderPath, fileData.originalItem.name, blob);
      } else {
        zipStream.errorLog.push(
          `${folderPath}/${fileData.originalItem.name} (0.00 MB) - ERROR: ${error} | Modules With Folder`
        );
        useStore
          .getState()
          .addError(
            `${folderPath}/${fileData.originalItem.name} (0.00 MB) - ERROR: ${error} | Modules With Folder`
          );
      }

      availableWorker.isBusy = false;
      activeTasks--;
      processNext();
    };
  };

  while (activeTasks < MAX_WORKERS && taskQueue.length > 0) {
    processNext();
  }

  await new Promise((resolve) => {
    const checkCompletion = setInterval(() => {
      if (activeTasks === 0 || zipStream.isCancelled) {
        clearInterval(checkCompletion);
        workers.forEach((w) => w.worker.terminate());
        resolve(true);
      }
    }, 500);
  });
};

// --- Main Download Function ---
export const downloadMultipleModuleFilesWithFolders = async ({
  moduleFiles,
  onDownloadStarted,
}: {
  moduleFiles: ModuleFileDownload[];
  onDownloadStarted?: () => void;
}): Promise<void> => {
  useStore.getState().changeDownloadingState(DownloadingState.DOWNLOADING);

  if (!moduleFiles.length) {
    return;
  }

  let startDownload = true;
  if (moduleFiles.length > 50) {
    startDownload = await ModalConfirmDownload();
  }
  if (!startDownload) return;

  const zipStream = new ZipStream(moduleFiles.length, 0);
  zipStream.start();
  if (zipStream.isCancelled) return;

  const fileChunks = chunkArray(moduleFiles, 10);

  try {
    for (const chunk of fileChunks) {
      if (zipStream.isCancelled) return;
      await processChunk(chunk, zipStream);
    }

    if (!zipStream.isCancelled) {
      await zipStream.finish();
    }
  } catch (error) {}
};
